/* ============================================================================
   COORDENADAS EN GRADOS, MINUTOS Y SEGUNDOS

   La aplicación pedía y mostraba grados decimales. **La aviación no trabaja
   así**: la AIP publica `020742S 0795201W`, las cartas van en grados y
   minutos, y un piloto que lee una coordenada de una carta tiene que poder
   escribirla como la ve.

   Obligarlo a convertir a decimal en la cabeza es pedirle que haga una cuenta
   —y una cuenta hecha a mano es un error esperando.

   ══════════════════════════════════════════════════════════════════════════
   SE ACEPTA TODO LO QUE UN PILOTO PUEDE ESCRIBIR

   Cada carta y cada equipo usa una notación distinta, y el piloto copia la que
   tiene delante:

     · `02°07'42"S`     con símbolos, como en una carta
     · `020742S`        pegado, como en la AIP
     · `02 07 42 S`     con espacios, que es lo que sale del teclado
     · `S02°07.7'`      grados y minutos decimales, como en un GPS
     · `-2.1283`        decimal, que es lo que había antes

   Rechazar una forma porque «no es el formato» manda al piloto a convertir a
   mano, que es exactamente lo que este módulo existe para evitar.

   ══════════════════════════════════════════════════════════════════════════
   LO QUE NO SE ACEPTA

   Una coordenada AMBIGUA. `0207` puede ser 2°07' o 20°7' según dónde se corte,
   y adivinar mueve el punto veinte grados. Se rechaza y se dice por qué.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

const texto = (v) => (typeof v === "string" ? v.trim() : (Number.isFinite(v) ? String(v) : ""));

/** Qué eje es: decide el rango válido y los dígitos de grado. */
export const EJE = { latitud: "latitud", longitud: "longitud" };

/** Los hemisferios de cada eje. */
const HEMISFERIOS = {
  latitud: { positivo: "N", negativo: "S" },
  longitud: { positivo: "E", negativo: "W" },
};

const MAXIMO = { latitud: 90, longitud: 180 };

/**
 * Lee una coordenada escrita de cualquiera de las formas usuales.
 *
 * @returns {{valor: number|null, motivo: string|null, formato: string|null}}
 */
export function leerCoordenada(entrada, eje = EJE.latitud) {
  const crudo = texto(entrada);
  if (!crudo) return { valor: null, motivo: null, formato: null };

  const maximo = MAXIMO[eje] ?? 180;
  const hemis = HEMISFERIOS[eje] || HEMISFERIOS.latitud;

  /* El hemisferio puede ir adelante o atrás: `S02°07'` y `02°07'S` son la
     misma coordenada y las dos se escriben. */
  const t = crudo.toUpperCase().replace(/\s+/g, " ");
  let signo = null;
  let cuerpo = t;

  const alFinal = /([NSEW])\s*$/.exec(t);
  const alPrincipio = /^([NSEW])\s*/.exec(t);

  if (alFinal) {
    signo = alFinal[1];
    cuerpo = t.slice(0, alFinal.index).trim();
  } else if (alPrincipio) {
    signo = alPrincipio[1];
    cuerpo = t.slice(alPrincipio[0].length).trim();
  }

  if (signo && signo !== hemis.positivo && signo !== hemis.negativo) {
    return {
      valor: null,
      formato: null,
      motivo: `«${signo}» no es un hemisferio de ${eje}: usa `
        + `${hemis.positivo} o ${hemis.negativo}.`,
    };
  }

  const negativo = signo === hemis.negativo;

  /* ─── Con separadores: 02°07'42" · 02 07 42 · 2:7:42 ─── */
  const conSeparadores = /^(-?\d{1,3})\s*[°º:\s]\s*(\d{1,2}(?:\.\d+)?)\s*(?:['′:\s]\s*(\d{1,2}(?:\.\d+)?)\s*["″]?)?$/
    .exec(cuerpo.replace(/["″']\s*$/, ""));

  if (conSeparadores) {
    const g = Math.abs(Number(conSeparadores[1]));
    const m = Number(conSeparadores[2]);
    const s = conSeparadores[3] === undefined ? 0 : Number(conSeparadores[3]);

    if (m >= 60) return { valor: null, formato: null, motivo: `${m} minutos: el máximo es 59.` };
    if (s >= 60) return { valor: null, formato: null, motivo: `${s} segundos: el máximo es 59.` };
    if (g > maximo) return { valor: null, formato: null, motivo: `${g}° supera el máximo de ${eje} (${maximo}°).` };

    const v = g + m / 60 + s / 3600;
    if (v > maximo) return { valor: null, formato: null, motivo: `Supera el máximo de ${eje} (${maximo}°).` };

    const negativoPorSigno = /^-/.test(conSeparadores[1]);
    return {
      valor: redondear((negativo || negativoPorSigno) ? -v : v),
      motivo: null,
      formato: conSeparadores[3] === undefined ? "gm" : "gms",
    };
  }

  /* ─── Pegado, como la AIP: 020742S / 0795201W ───

     El número de dígitos de grado depende del eje: la latitud usa dos y la
     longitud tres. Leer `0795201` como latitud daría 79°, que no existe en
     Ecuador y además no es lo que el dato dice. */
  const digitosGrado = eje === EJE.longitud ? 3 : 2;
  const pegado = new RegExp(`^(\\d{${digitosGrado}})(\\d{2})(\\d{2}(?:\\.\\d+)?)$`).exec(cuerpo);

  if (pegado && signo) {
    const g = Number(pegado[1]);
    const m = Number(pegado[2]);
    const s = Number(pegado[3]);

    if (m >= 60) return { valor: null, formato: null, motivo: `${m} minutos: el máximo es 59.` };
    if (s >= 60) return { valor: null, formato: null, motivo: `${s} segundos: el máximo es 59.` };
    if (g > maximo) return { valor: null, formato: null, motivo: `${g}° supera el máximo de ${eje}.` };

    const v = g + m / 60 + s / 3600;
    return { valor: redondear(negativo ? -v : v), motivo: null, formato: "aip" };
  }

  /* ─── Decimal: -2.1283 · 2.1283S ─── */
  const decimal = /^(-?\d{1,3}(?:[.,]\d+)?)$/.exec(cuerpo);
  if (decimal) {
    const n = Number(decimal[1].replace(",", "."));
    if (!Number.isFinite(n)) return { valor: null, formato: null, motivo: "No se entiende como coordenada." };
    if (Math.abs(n) > maximo) {
      return { valor: null, formato: null, motivo: `${n}° supera el máximo de ${eje} (${maximo}°).` };
    }

    /* Un entero suelto CON hemisferio es grados enteros —`2S` son dos grados
       sur— y sin hemisferio es un decimal con signo. Las dos lecturas dan lo
       mismo, así que no hay ambigüedad que resolver. */
    return { valor: redondear(negativo ? -Math.abs(n) : n), motivo: null, formato: "decimal" };
  }

  /* ─── Lo que queda es ambiguo o no es una coordenada ─── */
  if (/^\d{3,4}$/.test(cuerpo)) {
    return {
      valor: null,
      formato: null,
      /* `0207` puede ser 2°07' o 20°7' según dónde se corte, y adivinar mueve
         el punto veinte grados. */
      motivo: `«${crudo}» es ambiguo: podría leerse de dos formas. `
        + "Escribilo con separadores (02°07') o completo (020700S).",
    };
  }

  return {
    valor: null,
    formato: null,
    motivo: `No se entiende «${crudo}» como coordenada. `
      + "Prueba 02°07'42\"S, 020742S o -2.1283.",
  };
}

function redondear(v) {
  /* Seis decimales son once centímetros: más precisión que cualquier carta y
     que cualquier GPS de mano. */
  return Math.round(v * 1e6) / 1e6;
}

/**
 * Escribe una coordenada en grados, minutos y segundos.
 *
 * Es el formato de las cartas y de la AIP, y es el que el piloto puede leer en
 * voz alta por radio sin convertir nada.
 */
export function aGms(valor, eje = EJE.latitud, { segundosDecimales = 0 } = {}) {
  if (!Number.isFinite(valor)) return null;

  const hemis = HEMISFERIOS[eje] || HEMISFERIOS.latitud;
  const letra = valor < 0 ? hemis.negativo : hemis.positivo;
  const abs = Math.abs(valor);

  let g = Math.floor(abs);
  let m = Math.floor((abs - g) * 60);
  let s = ((abs - g) * 60 - m) * 60;

  /* El redondeo de los segundos puede dar 60: sin arrastrarlo, la coordenada
     quedaría escrita como `02°07'60"`, que no existe. */
  s = Number(s.toFixed(segundosDecimales));
  if (s >= 60) { s = 0; m += 1; }
  if (m >= 60) { m = 0; g += 1; }

  const gg = String(g).padStart(eje === EJE.longitud ? 3 : 2, "0");
  const mm = String(m).padStart(2, "0");
  const ss = s.toFixed(segundosDecimales).padStart(segundosDecimales > 0 ? 3 + segundosDecimales : 2, "0");

  return `${gg}°${mm}'${ss}"${letra}`;
}

/**
 * Escribe en el formato pegado de la AIP: `020742S`.
 *
 * Sirve para exportar y para cotejar contra la publicación, donde va así.
 */
export function aFormatoAip(valor, eje = EJE.latitud) {
  const g = aGms(valor, eje);
  return g ? g.replace(/[°'"]/g, "") : null;
}

/** Un par de coordenadas, para mostrar una posición completa. */
export function posicionEnGms(lat, lon, opciones = {}) {
  const a = aGms(lat, EJE.latitud, opciones);
  const b = aGms(lon, EJE.longitud, opciones);
  return a && b ? `${a} ${b}` : null;
}
